import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { Box, IconButton, Stack } from "@mui/material";
import { styled as MuiStyled } from "@mui/material/styles";
import React, { forwardRef, useImperativeHandle, useRef } from "react";
import { useDrag, useDrop } from "react-dnd";
import { GRAPH_LABEL_CAP_ENABLED } from "../../../../constants/featureFlags";
import ColorPopup from "../../Shared/ColorPopup";
import { GRAPH_ROW_DND_TYPE, LABEL_MAX, PRESET_REFS, fieldStyle } from "./graphSettingsShared";

// gordon-0915-graph-block-data-points-drag-reorder: same hover-swap useDrag/
// useDrop pattern as SegmentReisizingCard.jsx (the app's existing block/segment
// reorder list) — reused rather than adding a second DnD library.
const DragHandle = MuiStyled(DragIndicatorIcon)`
  font-size: 22px;
  cursor: grab;
  color: #bbbbbb;
`;

const GraphDataPointRow = forwardRef(function GraphDataPointRow(
  { row, index, total, moveRow, onLabelChange, onValueChange, onColorChange, onRemove, disableRemove },
  focusHandleRef
) {
  const rowRef = useRef(null);
  const dragHandleRef = useRef(null);

  const [{ isDragging }, drag, preview] = useDrag({
    type: GRAPH_ROW_DND_TYPE,
    item: () => ({ index }),
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  const [{ handlerId }, drop] = useDrop({
    accept: GRAPH_ROW_DND_TYPE,
    collect: (monitor) => ({ handlerId: monitor.getHandlerId() }),
    hover(item, monitor) {
      if (!rowRef.current) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;

      const hoverBoundingRect = rowRef.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (
        (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) ||
        (dragIndex > hoverIndex && hoverClientY > hoverMiddleY)
      ) {
        return;
      }
      moveRow(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  drop(rowRef);
  drag(dragHandleRef);
  preview(rowRef);

  useImperativeHandle(focusHandleRef, () => ({
    focus: () => rowRef.current?.focus(),
  }));

  const handleKeyDown = (e) => {
    if (!e.altKey) return;
    if (e.key === "ArrowUp" && index > 0) {
      e.preventDefault();
      moveRow(index, index - 1, { viaKeyboard: true });
    } else if (e.key === "ArrowDown" && index < total - 1) {
      e.preventDefault();
      moveRow(index, index + 1, { viaKeyboard: true });
    }
  };

  return (
    <Box
      ref={rowRef}
      role="listitem"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      data-handler-id={handlerId}
      aria-label={`${row?.label || "Untitled data point"}, position ${index + 1} of ${total}`}
      aria-roledescription="Draggable data point. Alt+Up or Alt+Down to reorder."
      data-cy={`graph-row-${index}`}
      sx={{
        border: "1px solid #EAECF0",
        borderRadius: "8px",
        p: 1,
        backgroundColor: "#FFFFFF",
        opacity: isDragging ? 0.5 : 1,
        outline: "none",
        "&:focus-visible": { boxShadow: "0 0 0 3px rgba(109,94,246,0.35)" },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
        <Box
          ref={dragHandleRef}
          aria-hidden="true"
          data-testid={`graph-row-${index}-handle`}
          sx={{
            width: 32,
            height: 32,
            minWidth: 32,
            minHeight: 32,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <DragHandle fontSize="small" />
        </Box>
        <input
          style={{ ...fieldStyle, flex: 2 }}
          value={row?.label ?? ""}
          placeholder="Label"
          {...(GRAPH_LABEL_CAP_ENABLED ? { maxLength: LABEL_MAX } : {})}
          onChange={(e) =>
            onLabelChange(GRAPH_LABEL_CAP_ENABLED ? e.target.value.slice(0, LABEL_MAX) : e.target.value)
          }
          data-cy={`graph-row-${index}-label`}
        />
        <input
          style={{ ...fieldStyle, flex: 1, width: 64 }}
          type="number"
          value={row?.value ?? ""}
          placeholder="0"
          onChange={(e) => onValueChange(e.target.value === "" ? "" : Number(e.target.value))}
          data-cy={`graph-row-${index}-value`}
        />
        <IconButton
          size="small"
          color="error"
          aria-label={`Remove data point ${index + 1}`}
          onClick={onRemove}
          disabled={disableRemove}
          data-cy={`graph-row-${index}-remove`}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Stack>
      {/* Universal color component — one swatch per datapoint */}
      <ColorPopup
        title="Color"
        background={row?.color || PRESET_REFS[index % PRESET_REFS.length]}
        handleChangeBackgroundColor={onColorChange}
        resetBackgroundColor={() => onColorChange(PRESET_REFS[index % PRESET_REFS.length])}
        showSuggestedColorPalette={true}
        showSlider={false}
        isHideGradient={true}
      />
    </Box>
  );
});

export default GraphDataPointRow;
